import { useState, useMemo, useRef } from 'react';
import {
  Server as ServerIcon,
  Globe,
  Puzzle,
  Shield,
  ChevronRight,
  Tag,
  Radio,
  Webhook,
  Search,
  X,
  AlertTriangle,
  EyeOff,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { AdminSubPageLayout } from '@reflex/ui/components/layout/AdminSubPageLayout';
import { PanelTitle, SectionLabel, PageDescription } from '@reflex/ui/components/layout/Typography';
import { usePlugins } from '@/hooks/config/usePlugins';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { PluginFilterBar } from '@/components/filters/PluginFilterBar';
import {
  filterPlugins,
  collectTags,
  type PluginFilters,
  type PluginStatusFilter,
  type PluginTypeFilter,
  type PluginTagFilter,
} from '@/components/filters/plugin-filter-utils';
import type { PluginManifest } from '@/lib/plugin-registry';
import { PluginIcon } from '@reflex/ui/components/shared/PluginIcon';
import { RequiredCapabilityBadge } from '@/components/plugins/CapabilityBadges';
import { ConfigBadge } from '@/components/status/ConfigBadge';
import { PLUGIN_STATUS_BADGE } from '@/lib/status-colors';
import { cn } from '@reflex/ui/lib/utils';
import { formatPluginName } from '@reflex/ui/lib/format';
import { ROUTES } from '@/lib/routes';

function TagPills({
  tags,
  selected,
  onSelect,
}: {
  tags: string[];
  selected: PluginTagFilter;
  onSelect: (tag: PluginTagFilter) => void;
}) {
  if (tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {tags.map((tag) => {
        const isActive = selected === tag;
        return (
          <Badge
            key={tag}
            variant={isActive ? 'default' : 'outline'}
            className={cn(
              'cursor-pointer gap-1 px-2.5 py-1 transition-colors',
              isActive
                ? 'border-primary/40 bg-primary/10 text-primary hover:bg-primary/20'
                : 'bg-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground',
            )}
            asChild
          >
            <button type="button" onClick={() => onSelect(isActive ? null : tag)}>
              <Tag className="h-3 w-3" />
              {tag}
            </button>
          </Badge>
        );
      })}
    </div>
  );
}

function PluginRow({ plugin }: { plugin: PluginManifest }) {
  const isEnabled = plugin.enabled !== false;
  const hasUnfulfilledSecrets =
    isEnabled && (plugin.server?.requiredSecrets?.some((s) => !s.fulfilled) ?? false);

  return (
    <Link
      to={ROUTES.admin.plugin(plugin.name)}
      data-testid={`plugin-row-${plugin.name}`}
      data-enabled={isEnabled ? 'true' : 'false'}
      data-needs-config={hasUnfulfilledSecrets ? 'true' : undefined}
      className="group"
    >
      <div
        className={cn(
          'flex items-center gap-3 rounded-lg border bg-card px-4 py-3 text-card-foreground transition-colors hover:border-border',
          !isEnabled && 'border-status-accent-orange/20',
          hasUnfulfilledSecrets && 'border-status-accent-orange/20',
        )}
      >
        <PluginIcon icon={plugin.icon} size="sm" className="shrink-0" />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <PanelTitle as="h2" size="sm">
              {formatPluginName(plugin.name)}
            </PanelTitle>
            <Badge variant="secondary" className="shrink-0 text-2xs font-normal tabular-nums">
              v{plugin.version}
            </Badge>
          </div>
          {/* When the plugin is disabled, surface the reason directly on the
              row so operators can triage missing-config / missing-secret
              problems without clicking into the detail page. Falls back to
              the description for healthy plugins. */}
          {!isEnabled && plugin.disabledReason ? (
            <PageDescription className="mt-0.5 truncate text-xs text-status-accent-orange">
              <span data-testid={`plugin-row-${plugin.name}-disabled-reason`}>
                {plugin.disabledReason}
              </span>
            </PageDescription>
          ) : (
            plugin.description && (
              <PageDescription className="mt-0.5 truncate text-xs">
                {plugin.description}
              </PageDescription>
            )
          )}
        </div>

        <div className="hidden shrink-0 items-center gap-1 sm:flex">
          {plugin.server && (
            <Badge variant="outline" className="gap-1 text-2xs font-normal">
              <ServerIcon className="h-3 w-3" />
              Server
            </Badge>
          )}
          {plugin.web && (
            <Badge variant="outline" className="gap-1 text-2xs font-normal">
              <Globe className="h-3 w-3" />
              Web
            </Badge>
          )}
          {plugin.server?.authProvider && (
            <Badge variant="outline" className="gap-1 text-2xs font-normal">
              <Shield className="h-3 w-3" />
              Auth
            </Badge>
          )}
          {plugin.server?.webhookProvider && (
            <Badge variant="outline" className="gap-1 text-2xs font-normal">
              <Radio className="h-3 w-3" />
              Webhook Provider
            </Badge>
          )}
          {(plugin.server?.webhookReceivers?.length ?? 0) > 0 && (
            <Badge variant="outline" className="gap-1 text-2xs font-normal">
              <Webhook className="h-3 w-3" />
              Webhook Receiver
            </Badge>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {plugin.requiredCapabilities && plugin.requiredCapabilities.length > 0 && (
            <RequiredCapabilityBadge
              requiredCapabilities={plugin.requiredCapabilities}
              testIdSuffix={plugin.name}
            />
          )}
          {plugin.hidden && (
            <Badge
              variant="secondary"
              className="gap-1 text-2xs font-normal text-muted-foreground"
              data-testid={`plugin-row-${plugin.name}-hidden`}
            >
              <EyeOff className="h-3 w-3" />
              Hidden
            </Badge>
          )}
          {hasUnfulfilledSecrets && (
            <Badge
              variant="secondary"
              className="gap-1 text-2xs font-normal text-status-accent-orange"
              data-testid={`plugin-row-${plugin.name}-needs-config`}
            >
              <AlertTriangle className="h-3 w-3" />
              Needs configuration
            </Badge>
          )}
          <ConfigBadge config={PLUGIN_STATUS_BADGE} value={isEnabled ? 'active' : 'disabled'} />
          <ChevronRight className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-muted-foreground" />
        </div>
      </div>
    </Link>
  );
}

export function AdminPluginsPage() {
  const { manifests, isLoading, isError } = usePlugins();

  const [filters, setFilters] = useState<PluginFilters>({
    status: null,
    type: null,
    tag: null,
    query: '',
  });
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => filterPlugins(manifests, filters), [manifests, filters]);
  const availableTags = useMemo(() => collectTags(manifests), [manifests]);

  const activeCount = manifests.filter((p) => p.enabled !== false).length;
  const hasActiveFilters =
    filters.status !== null ||
    filters.type !== null ||
    filters.tag !== null ||
    filters.query !== '';

  return (
    <AdminSubPageLayout
      title="Plugins"
      description="Installed plugins that extend the server and web UI. Plugin manifests are loaded from the server at runtime."
      docs="plugins/overview"
    >
      <AsyncContent
        isLoading={isLoading}
        isError={isError}
        loadingMessage="Loading plugins..."
        errorMessage="Failed to load plugins."
      >
        {manifests.length === 0 ? (
          <EmptyState
            icon={<Puzzle className="h-5 w-5 text-muted-foreground" />}
            title="No plugins installed"
            description="Plugins extend the server with webhook providers, receivers, routes, and more."
          />
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <SectionLabel className="shrink-0">
                {hasActiveFilters ? (
                  <>
                    {filtered.length} of {manifests.length} plugins
                  </>
                ) : (
                  <>
                    {activeCount} active, {manifests.length} total
                  </>
                )}
              </SectionLabel>
              <div className="relative min-w-0 flex-1">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search plugins…"
                  value={filters.query}
                  onChange={(e) => setFilters((prev) => ({ ...prev, query: e.target.value }))}
                  className="h-8 pl-8 pr-8 text-sm"
                />
                {filters.query && (
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Clear search"
                    onClick={() => {
                      setFilters((prev) => ({ ...prev, query: '' }));
                      searchInputRef.current?.focus();
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              <PluginFilterBar
                filters={filters}
                onStatusChange={(status: PluginStatusFilter) =>
                  setFilters((prev) => ({ ...prev, status }))
                }
                onTypeChange={(type: PluginTypeFilter) => setFilters((prev) => ({ ...prev, type }))}
              />
            </div>
            <TagPills
              tags={availableTags}
              selected={filters.tag}
              onSelect={(tag: PluginTagFilter) => setFilters((prev) => ({ ...prev, tag }))}
            />
            {filtered.length === 0 ? (
              <EmptyState
                icon={<Puzzle className="h-5 w-5 text-muted-foreground" />}
                title="No matching plugins"
                description="Try adjusting your filters."
              />
            ) : (
              <div className="flex flex-col gap-1.5">
                {filtered.map((plugin) => (
                  <PluginRow key={plugin.name} plugin={plugin} />
                ))}
              </div>
            )}
          </div>
        )}
      </AsyncContent>
    </AdminSubPageLayout>
  );
}
